import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const LanguageContext = createContext();

export const supportedLanguages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
];

const Loader = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="w-8 h-8 rounded-full border-4 animate-spin border-primary border-t-transparent"></div>
  </div>
);


export const LanguageProvider = ({ children }) => {
  const { i18n, t, ready } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');

  // Initialize language on first load - check localStorage first, then i18n
  useEffect(() => {
    // Check if there's a saved language in localStorage
    const savedLanguage = localStorage.getItem('appLanguage');
    const currentLang = savedLanguage || i18n.language.split('-')[0];
    
    if (supportedLanguages.some(l => l.code === currentLang)) {
      setLanguage(currentLang);
      document.documentElement.lang = currentLang;
      if (currentLang !== i18n.language) {
        i18n.changeLanguage(currentLang);
      }
    } else {
      setLanguage('en');
      document.documentElement.lang = 'en';
      i18n.changeLanguage('en');
    }
  }, [i18n]);

  const changeLanguage = useCallback(async (langCode) => {
    if (supportedLanguages.some(l => l.code === langCode)) {
      try {
        await i18n.changeLanguage(langCode);
        setLanguage(langCode);
        localStorage.setItem('appLanguage', langCode);
        document.documentElement.lang = langCode;
        console.log(`Language changed to: ${langCode} (stored in localStorage only)`);
      } catch (err) {
        console.error('Error changing language in i18n:', err);
      }
    } else {
      console.warn(`Attempted to change to unsupported language: ${langCode}`);
    }
  }, [i18n]);
  
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      const baseLng = lng.split('-')[0];
      if (supportedLanguages.some(l => l.code === baseLng)) {
        setLanguage(baseLng);
        document.documentElement.lang = baseLng;
      }
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const value = {
    language,
    changeLanguage,
    supportedLanguages,
    t,
    isReady: ready,
    Loader
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
  
